import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import { Pagination } from '@/components/Pagination';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState } from '@/components/ListStates';
import { contactGroupsService } from '@/services/contact-groups.service';
import { contactsService } from '@/services/contacts.service';
import { facebookAudiencesService } from '@/services/facebook-audiences.service';
import { Users, Megaphone, UserPlus, Search, X } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

export default function GroupMembers() {
  const { uid } = useParams<{ uid: string }>();
  const [group, setGroup] = useState<any>(null);
  const [members, setMembers] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [allContacts, setAllContacts] = useState<any[]>([]);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [searchQ, setSearchQ] = useState('');
  const [syncingMeta, setSyncingMeta] = useState(false);
  const [loadError, setLoadError] = useState<'not_found' | 'failed' | null>(null);

  const refresh = () => {
    setLoadError(null);
    return Promise.all([
      contactGroupsService.get(uid!, { expectedStatuses: [404] }).then(setGroup),
      contactGroupsService.members(uid!, page, 25).then(setMembers),
    ]).catch((e) => setLoadError(e?.response?.status === 404 ? 'not_found' : 'failed'));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [uid, page]);

  const syncToMeta = async () => {
    if (!group) return;
    setSyncingMeta(true);
    try {
      const r = await facebookAudiencesService.syncFromGroup({ groupUid: group.groupUid });
      toast.success(`Synced ${r.recipientCount} contacts to Meta`);
    } catch { /* toast shown */ } finally { setSyncingMeta(false); }
  };

  const openAdd = async () => {
    const r = await contactsService.list({ limit: 200 });
    setAllContacts(r.items);
    setPicked(new Set());
    setSearchQ('');
    setAddOpen(true);
  };

  const submitAdd = async () => {
    await contactsService.bulkAddToGroup(Array.from(picked), uid!);
    toast.success(`Added ${picked.size}`);
    setAddOpen(false);
    refresh();
  };

  const removeOne = async (contactUid: string) => {
    await contactsService.bulkRemoveFromGroup([contactUid], uid!);
    toast.success('Removed');
    refresh();
  };

  const filteredContacts = allContacts.filter((c) => {
    if (!searchQ) return true;
    const lower = searchQ.toLowerCase();
    return (
      `${c.firstName} ${c.lastName ?? ''}`.toLowerCase().includes(lower) ||
      c.phone?.includes(lower)
    );
  });

  if (!group) {
    return (
      <>
        <Topbar title="Group" breadcrumb="Audience / Groups" />
        <div className="px-7 py-7 w-full space-y-3">
          {loadError === 'not_found' ? (
            <EmptyState
              icon={<Users size={28} />}
              title="Group not found"
              description="This group may have been deleted, or the link is out of date."
              action={
                <Link to="/groups">
                  <Button variant="secondary">Back to groups</Button>
                </Link>
              }
            />
          ) : loadError ? (
            <LoadErrorState what="this group" onRetry={() => void refresh()} />
          ) : (
            <>
              <div className="skeleton h-16 rounded-2xl" />
              <div className="skeleton h-64 rounded-2xl" />
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={group.name}
        breadcrumb="Audience / Groups"
        description={group.description ?? undefined}
        right={
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={syncingMeta}
              onClick={syncToMeta}
              iconLeft={<Megaphone size={14} />}
              loading={syncingMeta}
            >
              Sync to Meta
            </Button>
            <Button onClick={openAdd} iconLeft={<UserPlus size={14} />}>
              Add contacts
            </Button>
          </div>
        }
      />

      <div className="px-7 py-7 w-full">
        {/* Group header chip */}
        <div className="mb-5 flex items-center gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
            style={{ background: `${group.color}18`, color: group.color }}
          >
            <Users size={18} />
          </div>
          <div>
            <div className="font-semibold text-text-primary">{group.name}</div>
            <div className="text-xs text-text-muted">
              {members?.total?.toLocaleString() ?? '—'} members
            </div>
          </div>
          <span
            className="ml-auto inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold"
            style={{ background: `${group.color}14`, color: group.color, borderColor: `${group.color}30` }}
          >
            {members?.total?.toLocaleString() ?? '—'} contacts
          </span>
        </div>

        {/* Members table */}
        {loadError ? (
          <LoadErrorState what="the members of this group" onRetry={() => void refresh()} />
        ) : !members ? (
          <Card padded={false} className="overflow-hidden">
            <div className="space-y-0 divide-y divide-border">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-4">
                  <div className="skeleton h-8 w-8 rounded-full" />
                  <div className="skeleton h-4 w-40 rounded" />
                  <div className="skeleton h-4 w-28 rounded ml-auto" />
                </div>
              ))}
            </div>
          </Card>
        ) : (
          <>
            <Card padded={false} className="overflow-hidden">
              <div
                className="grid border-b border-border bg-surface-alt px-5 py-2.5"
                style={{ gridTemplateColumns: '2fr 1fr 100px', gap: '12px' }}
              >
                {['Name', 'Phone', ''].map((h) => (
                  <span key={h} className="text-2xs font-semibold uppercase tracking-[0.14em] text-text-muted">{h}</span>
                ))}
              </div>

              {members.items.length === 0 ? (
                <div className="flex flex-col items-center gap-2 px-5 py-10 text-text-muted">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-alt">
                    <Users size={18} />
                  </div>
                  <div className="text-sm">No members yet — click <strong>Add contacts</strong>.</div>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {members.items.map((c: any) => (
                    <div
                      key={c.contactUid}
                      className="grid items-center px-5 py-3.5 text-sm transition-colors hover:bg-surface-alt/60"
                      style={{ gridTemplateColumns: '2fr 1fr 100px', gap: '12px' }}
                    >
                      <Link to={`/contacts/${c.contactUid}`} className="font-semibold text-text-primary hover:text-primary transition-colors">
                        {c.firstName} {c.lastName ?? ''}
                      </Link>
                      <span className="font-mono text-xs text-text-secondary">{c.phone}</span>
                      <div>
                        <Button
                          size="sm"
                          variant="ghost"
                          iconLeft={<X size={13} />}
                          onClick={() => removeOne(c.contactUid)}
                          className="text-danger hover:text-danger hover:bg-danger-soft"
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Pagination page={members.page} total={members.total} limit={members.limit} onChange={setPage} />
          </>
        )}
      </div>

      {/* Add members modal */}
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title={`Add contacts to ${group.name}`} widthClass="max-w-2xl">
        <div className="mb-3">
          <Input
            iconLeft={<Search size={15} />}
            value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            placeholder="Search contacts…"
          />
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-border rounded-xl border border-border">
          {filteredContacts.map((c) => (
            <label
              key={c.contactUid}
              className={clsx(
                'flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors',
                picked.has(c.contactUid) ? 'bg-primary-soft' : 'hover:bg-surface-alt',
              )}
            >
              <input
                type="checkbox"
                checked={picked.has(c.contactUid)}
                onChange={() => {
                  const next = new Set(picked);
                  if (next.has(c.contactUid)) next.delete(c.contactUid);
                  else next.add(c.contactUid);
                  setPicked(next);
                }}
                className="h-4 w-4 rounded border-border accent-primary"
              />
              <div className="flex-1 min-w-0">
                <div className="truncate text-sm font-medium text-text-primary">
                  {c.firstName} {c.lastName ?? ''}
                </div>
              </div>
              <span className="font-mono text-xs text-text-muted">{c.phone}</span>
            </label>
          ))}
          {filteredContacts.length === 0 && (
            <div className="px-4 py-6 text-center text-sm text-text-muted">No contacts found.</div>
          )}
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-text-muted">{picked.size} selected</span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={submitAdd} disabled={picked.size === 0}>
              Add {picked.size > 0 ? picked.size : ''}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
